/**
 * PcmMediaRecorder
 *
 * MediaRecorder-compatible wrapper around AudioWorklet PCM capture.
 * - No batching
 * - No timers
 * - Immediate streaming
 * - PCM16 mono
 *
 * Designed for AssemblyAI / Deepgram-style realtime STT.
 */

class PcmMediaRecorder {
  constructor(stream, opts = {}) {
    this.stream = stream;
    this.sampleRate = opts.sampleRate || 48000;

    // MediaRecorder state
    this.state = "inactive";

    // MediaRecorder-style handlers
    this.ondataavailable = null;
    this.onstart = null;
    this.onstop = null;
    this.onerror = null;

    // Event listeners
    this._listeners = {
      dataavailable: new Set(),
      start: new Set(),
      stop: new Set(),
      error: new Set(),
    };

    this._audioPlayer = null;
  }

  /* ---------------- MediaRecorder API ---------------- */

  addEventListener(type, handler) {
    const set = this._listeners[type];
    if (set) set.add(handler);
  }

  removeEventListener(type, handler) {
    const set = this._listeners[type];
    if (set) set.delete(handler);
  }

  _emit(type, event) {
    const prop = this[`on${type}`];
    if (typeof prop === "function") {
      try { prop(event); } catch (_) {}
    }

    const set = this._listeners[type];
    if (set) {
      for (const fn of set) {
        try { fn(event); } catch (_) {}
      }
    }
  }

  /* ---------------- Core logic ---------------- */

  async start() {
    if (this.state === "recording") return;
    this.state = "recording";

    try {
      this._audioPlayer = window.audioPlayer;
      if (!this._audioPlayer) {
        throw new Error("audioPlayer not available");
      }

      // Start PCM capture — AUDIO IS SENT IMMEDIATELY
      await this._audioPlayer.startMicPcmCapture(this.stream, (pcm16) => {
        if (this.state !== "recording") return;

        // Slice exact bytes (critical)
        const ab = pcm16.buffer.slice(
          pcm16.byteOffset,
          pcm16.byteOffset + pcm16.byteLength
        );

        this._emit("dataavailable", {
          data: {
            size: ab.byteLength,
            arrayBuffer: async () => ab,
          },
        });
      });

      this._emit("start");

    } catch (err) {
      this.state = "inactive";
      this._emit("error", err);
    }
  }

  stop() {
    if (this.state !== "recording") return;
    this.state = "inactive";

    try {
      if (this._audioPlayer) {
        this._audioPlayer.stopMicPcmCapture();
      }
    } catch (_) {}

    this._emit("stop");
  }
}

export default PcmMediaRecorder;
